import React, { useEffect } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { AppProvider } from './contexts/AppContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { useTheme } from './hooks/useTheme';
import { appRouter } from './router/createAppRouter.jsx';

// Faza 5.3: Error Boundary around AppLayout to log full stack on __WEBPACK_DEFAULT_EXPORT__ or any error
class AppLayoutErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  static isChunkLoadError(error) {
    return Boolean(
      error &&
      (error?.name === 'ChunkLoadError' ||
        (typeof error?.message === 'string' && error.message.includes('Loading chunk')))
    );
  }

  static triggerChunkRecovery() {
    const recoveryKey = '__chunk_recovery_attempted__';
    if (sessionStorage.getItem(recoveryKey)) return;
    sessionStorage.setItem(recoveryKey, '1');

    const hardReload = () => {
      const current = new URL(window.location.href);
      current.searchParams.set('chunk-reload', Date.now().toString());
      window.location.replace(current.toString());
    };

    if (!('serviceWorker' in navigator)) {
      hardReload();
      return;
    }

    navigator.serviceWorker.getRegistrations()
      .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
      .then(() => {
        if ('caches' in window) {
          return caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))));
        }
        return null;
      })
      .finally(hardReload);
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AppLayoutErrorBoundary]', {
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
    });

    if (AppLayoutErrorBoundary.isChunkLoadError(error)) {
      AppLayoutErrorBoundary.triggerChunkRecovery();
    }
  }

  render() {
    const isChunkLoadError = AppLayoutErrorBoundary.isChunkLoadError(this.state.error);
    if (this.state.hasError && this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center max-w-md">
            <h1 className="text-lg font-semibold text-error mb-2">Something went wrong</h1>
            <p className="text-sm text-text-secondary mb-4">
              {isChunkLoadError
                ? 'The app was updated. Please refresh the page to load the latest version.'
                : 'Check the console for details.'}
            </p>
            {isChunkLoadError ? (
              <button
                type="button"
                className="listener-btn listener-btn-primary"
                onClick={() => window.location.reload()}
              >
                Refresh page
              </button>
            ) : null}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ThemeInitializer() {
  useTheme();
  return null;
}

function App() {
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const errorMessage = args.join(' ');
      if (errorMessage.includes('400') && (
          errorMessage.includes('/login') || errorMessage.includes('/register') || errorMessage.includes('/verify-email') ||
          errorMessage.includes('/password-reset') || errorMessage.includes('/two-factor-auth') || errorMessage.includes('/change-password') ||
          errorMessage.includes('/change-email') || errorMessage.includes('/accounts/login') || errorMessage.includes('/accounts/register'))) return;
      if (errorMessage.includes('429') || errorMessage.includes('Rate limited') || errorMessage.includes('Too Many Requests')) return;
      if (errorMessage.includes('403') && errorMessage.includes('audio')) {
        console.warn('Audio access denied - this is expected for unauthorized users');
        return;
      }
      if (errorMessage.includes('radio-deploy-static.s3.amazonaws.com') && errorMessage.includes('403')) {
        console.warn('S3 audio access denied - this is expected for unauthorized users');
        return;
      }
      if (errorMessage.includes('Failed to fetch') && errorMessage.includes('403')) {
        console.warn('Audio fetch failed - this is expected for unauthorized users');
        return;
      }
      if (errorMessage.includes('403') && (errorMessage.includes('fetch') || errorMessage.includes('load'))) {
        console.warn('Access denied - this is expected for unauthorized users');
        return;
      }
      if (errorMessage.includes('403')) {
        console.warn('Access denied - this is expected for unauthorized users');
        return;
      }
      if (typeof originalConsoleError === 'function') originalConsoleError.apply(console, args);
    };

    const originalOnUnhandledRejection = window.onunhandledrejection;
    window.onunhandledrejection = (event) => {
      const errorMessage = String(event.reason);
      if (errorMessage.includes('400') && (
          errorMessage.includes('/login') || errorMessage.includes('/register') || errorMessage.includes('/verify-email') ||
          errorMessage.includes('/password-reset') || errorMessage.includes('/two-factor-auth') || errorMessage.includes('/change-password') ||
          errorMessage.includes('/change-email') || errorMessage.includes('/accounts/login') || errorMessage.includes('/accounts/register'))) {
        event.preventDefault();
        return;
      }
      if (errorMessage.includes('429') || errorMessage.includes('Rate limited') || errorMessage.includes('Too Many Requests')) {
        event.preventDefault();
        return;
      }
      if (errorMessage.includes('403')) {
        event.preventDefault();
        return;
      }
      if (originalOnUnhandledRejection) originalOnUnhandledRejection(event);
    };

    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      const errorMessage = String(message);
      if (errorMessage.includes('403')) {
        console.warn('Access denied - this is expected for unauthorized users');
        return true;
      }
      if (originalOnError) return originalOnError(message, source, lineno, colno, error);
      return false;
    };

    return () => {
      if (typeof originalConsoleError === 'function') console.error = originalConsoleError;
      window.onunhandledrejection = originalOnUnhandledRejection;
      window.onerror = originalOnError;
    };
  }, []);

  return (
    <AppProvider>
      <CookieConsentProvider>
        <ThemeInitializer />
        <div className="App">
          <AppLayoutErrorBoundary>
            <RouterProvider router={appRouter} />
          </AppLayoutErrorBoundary>
        </div>
      </CookieConsentProvider>
    </AppProvider>
  );
}

export default App;
export { App };
